import {
	DateString,
	Highlight,
	HighlightedText,
	ListItem,
	Space,
	Typography,
	PipeSeparator
} from "client/Components";
import { ReactElement, useMemo } from "react";
import { HubThumbnail } from "../../HubThumbnail/HubThumbnail";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import {
	isNotNil,
	isNotNilOrWhiteSpace,
	setTrackingId
} from "client/Misc/utils";
import { TimeZone } from "client/DateAndTime/timezones";
import styles from "./AnnouncementListItem.module.less";
import { localize } from "client_legacy/ndaw/culture";
import { HubSubtitleWithCategories } from "../../HubSubtitleWithCategories";
import { HubItemMediaPreviewViewModel } from "client/Api/Hub/common";
import { HubItemCategoryListItemModel } from "client/Api/Hub/hub";
import { useGlobalSearchItemActions } from "client/Features/Layout/TitleBar/GlobalSearch/GlobalSearchItem/useGlobalSearchItemActions";

export type AnnouncementHighlightableFields =
	| "title";

export type AnnouncementListItemProps = {
	title: string;
	media: HubItemMediaPreviewViewModel;
	publicationStart: Date | string;
	categories: readonly HubItemCategoryListItemModel[];
	authorName: string | null;
	trackingId: string;
	highlights?: Record<AnnouncementHighlightableFields, Highlight | undefined>;
	timezone: TimeZone;
	onClick?: () => void;
	onRemove?: () => void;
	itemIndex?: number;
	tabIndex?: number;
	onExecute?: () => void;
};

export const AnnouncementListItem = (props: AnnouncementListItemProps): ReactElement => {
	const {
		title,
		media,
		publicationStart,
		categories,
		authorName,
		trackingId,
		highlights,
		timezone,
		onClick,
		onRemove,
		itemIndex,
		tabIndex,
		onExecute
	} = props;

	const {
		onKeyDown,
		ref
	} = useGlobalSearchItemActions({
		itemIndex: itemIndex ?? -1,
		onExecute: onExecute ?? (() => {/** Nope */})
	});

	const categoryNames = useMemo(
		(): readonly string[] => {
			return categories.map(x => x.name);
		},
		[categories]
	);

	return (
		<ListItem
			trackingId={trackingId}
			noMobileBorderBottom
			tabIndex={tabIndex}
			ref={ref}
			onKeyDown={onKeyDown}
		>
			<ListItem.Content
				thumbnail={
					<div className={styles.thumbnailWrapper}>
						<HubThumbnail
							title={title}
							media={media}
							hubItemKind={HubItemKind.Announcement}
							mediaPlaceholderSize="extra-small"
						/>
					</div>
				}
				onClick={onClick}
			>
				<ListItem.Title>
					<Typography
						weight="bold"
						trackingId="title"
					>
						<HighlightedText
							text={title}
							highlight={highlights?.title}
						/>
					</Typography>
				</ListItem.Title>
				<ListItem.SubTitle>
					<HubSubtitleWithCategories
						categoryNames={categoryNames}
					>
						<>
							{publicationStart && (
								<span
									{...setTrackingId("timestamp")}
								>
									<DateString
										value={publicationStart}
										format="RelativeTimestamp"
										timezone={timezone}
									/>
								</span>
							)}
							{isNotNilOrWhiteSpace(authorName) && (
								<>
									<PipeSeparator />
									<span {...setTrackingId("author-name")}>
										{localize("_(Edited)By")}
										<Space />
										{authorName}
									</span>
								</>
							)}
						</>
					</HubSubtitleWithCategories>
				</ListItem.SubTitle>
			</ListItem.Content>
			{isNotNil(onRemove) && (
				<ListItem.Action
					icon="xmark"
					onClick={() => onRemove()}
					bubbleClick={false}
					trackingId="remove"
				/>
			)}
		</ListItem>
	);
};
AnnouncementListItem.displayName = "AnnouncementListItem";